// Push mobile — notifications natives via Firebase Cloud Messaging.
//
// Côté app (Capacitor, iOS + Android) via @capacitor-firebase/messaging :
//   1. initPushNotifications(navigate) — appelé au montage du MobileShell
//      (donc uniquement connecté, en contexte natif) :
//        - demande la permission système,
//        - récupère le token FCM et l'enregistre sur
//          POST /api/user/register-device/ (→ user.DeviceToken),
//        - écoute le rafraîchissement du token,
//        - au tap sur une notification : navigation interne vers `data.link`
//          (le back met Notification.link dans le payload data —
//          cf. Life_backend/user/push_service.py).
//   2. unregisterPushDevice() — à appeler AVANT clearClientSession à la
//      déconnexion (l'endpoint est authentifié) : l'appareil ne reçoit plus
//      les push de ce compte.
//
// Hors natif (web desktop / responsive) : tout est no-op.
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { Capacitor } from '@capacitor/core'
import { isNativeApp } from 'utils/mobile'
import { makePostRequest } from 'utils/api'

let currentToken = null
let subscriptions = []
let initialized = false

async function registerToken(token) {
  if (!token || token === currentToken) return
  currentToken = token
  try {
    await makePostRequest(
      '/api/user/register-device/',
      { token, platform: Capacitor.getPlatform() },
      {},
      true,
    )
  } catch (_) {
    // Réseau indisponible : on retentera au prochain démarrage.
    currentToken = null
  }
}

export async function initPushNotifications(navigate) {
  if (!isNativeApp() || initialized) return
  initialized = true

  try {
    const { receive } = await FirebaseMessaging.requestPermissions()
    if (receive !== 'granted') return

    // Tap sur une notification (app en fond ou fermée) → deep link interne.
    subscriptions.push(
      await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        (event) => {
          const link = event?.notification?.data?.link
          if (link && typeof link === 'string' && link.startsWith('/')) {
            navigate(link)
          }
        },
      ),
    )

    // FCM peut faire tourner le token ; on ré-enregistre à chaque fois.
    subscriptions.push(
      await FirebaseMessaging.addListener('tokenReceived', (event) => {
        registerToken(event?.token)
      }),
    )

    const { token } = await FirebaseMessaging.getToken()
    await registerToken(token)
  } catch (_) {
    // Firebase non configuré (GoogleService-Info.plist / google-services.json
    // absents) ou simulateur sans APNs : l'app reste fonctionnelle sans push.
    initialized = false
  }
}

export async function teardownPushNotifications() {
  const subs = subscriptions
  subscriptions = []
  initialized = false
  await Promise.all(
    subs.map((s) => Promise.resolve(s?.remove?.()).catch(() => {})),
  )
}

/** À la déconnexion, avant la purge du token de session. */
export async function unregisterPushDevice() {
  if (!isNativeApp() || !currentToken) return
  try {
    await makePostRequest(
      '/api/user/unregister-device/',
      { token: currentToken },
      {},
      true,
    )
  } catch (_) {}
  currentToken = null
}

export default initPushNotifications
